import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import BusinessLoader from "../../../common/BusinessLoader";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { handleApi } from "../../../../apis";
import { GET_TRANSACTION_DETAIL } from "../../../../apis/configuration";
import { copyReceiptImage, printWalletReceiptPreview } from "../../../../utils";
import UCashTransactionReceipt from "../../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../../common/DownloadReceipt";
import { formatNum } from "../../../../oscar-pos-core/constants";

const ANALYTICS_EVENTS = {
  bill_payment: "w_earn_bill_copy_receipt",
  voucher_payment: "w_earn_vouchers_copy_receipt",
  recharge: "w_earn_ep_easyload_receipt",
};

const PRINT_ANALYTICS_EVENTS = {
  bill_payment: "w_earn_bill_print",
  voucher_payment: "w_earn_vouchers_print",
  recharge: "w_earn_ep_easyload_print",
};

const TransactionDetail = () => {
  const [I18n] = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [transaction, setTransaction] = useState({});

  const receiptRef = useRef();
  const isMounted = useRef(true);
  const navigate = useNavigate();
  const { type, id } = useParams();
  const { showSnackbar } = useSnackbar();
  const { business } = useAuthContext();

  useEffect(() => {
    fetchTransaction();
    return () => { isMounted.current = false; };
  }, []);

  const fetchTransaction = async () => {
    try {
      const response = await handleApi(GET_TRANSACTION_DETAIL, { id }, null, navigate);
      if (!isMounted.current) return;
      if (response?.success) {
        setTransaction(response?.transaction || {});
        setIsLoading(false);
      } else {
        showSnackbar({ message: response?.message || I18n.transaction_not_available, severity: "error" });
        navigate(-1);
      }
    } catch (error) {
      if (!isMounted.current) return;
      showSnackbar({ message: error?.response?.data?.message || I18n.error_fetching_transaction, severity: "error" });
      navigate(-1);
    }
  };

  const copyAnalyticsEvent = ANALYTICS_EVENTS[type] || ANALYTICS_EVENTS.recharge;
  const printAnalyticsEvent = PRINT_ANALYTICS_EVENTS[type] || PRINT_ANALYTICS_EVENTS.recharge;

  const handleCopy = () =>
    copyReceiptImage(receiptRef, { showSnackbar, I18n, analyticsEvent: copyAnalyticsEvent });

  const isBill = type === "bill_payment";
  const isVoucher = type === "voucher_payment";

  const amount = Math.abs(transaction?.amount || 0);
  const tid = transaction?.id || id || "";
  const customerName = transaction?.customer_name || "";
  const consumerNumber = transaction?.consumer_number || "";
  const consumerName = transaction?.consumer_name || "";
  const billCompany = transaction?.bill_company || transaction?.billing_company || "";
  const voucherName = transaction?.voucher_name || transaction?.voucher || "";
  const formattedDate = transaction?.created_at
    ? moment(transaction.created_at).format("D MMMM YYYY, hh:mm A")
    : "";
  let headerText = <>{I18n.easyload_done_with} <strong>{I18n.rupin}</strong></>;
  let headerTextPlain = I18n.easyload_done_with_rupin;
  let recipientName = customerName;
  let recipientNamePlain = customerName;
  let bankInfo = `${transaction?.service} - ${transaction?.customer}`
  let byValue = business?.name || "-";
  let packageName = transaction?.offer_name || "";
  if (isBill) {
    headerText = <>{I18n.bill_paid_with} <strong>{I18n.rupin}</strong></>;
    headerTextPlain = I18n.bill_paid_with_rupin;
    const billBase = billCompany
      ? `${billCompany}${consumerNumber ? ` - ${consumerNumber}` : ""}`
      : consumerNumber;
    recipientNamePlain = `${billBase}${consumerName ? ` (${consumerName})` : ""}`;
    recipientName = consumerName ? (
      <>{billBase}<span className="newLightColor"> ({consumerName})</span></>
    ) : billBase;
    bankInfo = "";
    byValue = business?.name || "-";
  } else if (isVoucher) {
    headerText = <>{I18n.voucher_sent_with || "Voucher sent with"} <strong>{I18n.rupin}</strong></>;
    headerTextPlain = I18n.voucher_sent_with_rupin || "Voucher sent with RUPIN";
    bankInfo = voucherName;
  }
  const receiptRows = [
    { label: I18n.by || "By", value: byValue },
    { label: I18n.tid, value: tid },
    { label: I18n.date || "Date", value: formattedDate },
    { label: I18n.fee || "Fee", value: transaction?.fee ? `Rs. ${transaction.fee}` : `Rs. 0 (${I18n.free})` },
    { label: I18n.total_amount || "Total amount", value: `Rs. ${formatNum(amount) || 0}` },
  ];

  const handlePrint = () =>
    printWalletReceiptPreview(
      business,
      {
        headerText: headerTextPlain,
        amount,
        customerName: isBill ? customerName : "",
        recipientName: recipientNamePlain,
        bankInfo,
        rows: receiptRows,
      },
      { showSnackbar, setIsPrintLoading, analyticsEvent: printAnalyticsEvent },
    );

  if (isLoading) return <BusinessLoader loadBusiness />;

  return (
    <Fragment>
      <UCashTransactionReceipt
        amount={amount}
        headerText={headerText}
        recipientName={recipientName}
        customerName={isBill ? customerName : ""}
        bankInfo={bankInfo}
        rows={receiptRows}
        title={I18n.transaction_detail}
        onBack={() => navigate(-1)}
        onCopy={handleCopy}
        onPrint={handlePrint}
        isPrintLoading={isPrintLoading}
        packageName={packageName}
      />
      <DownloadReceipt
        ref={receiptRef}
        amount={amount}
        headerText={headerTextPlain}
        recipientName={recipientNamePlain}
        customerName={isBill ? customerName : ""}
        bankInfo={bankInfo}
        rows={receiptRows}
      />
    </Fragment>
  );
};

export default TransactionDetail;
